import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Prompt, PromptManagementService, PromptState } from 'app/shared/services/prompt-management.service';
import { InferenceParams, ModelThinkingConfig, StatoSessioneService } from 'app/shared/services/stato-sessione.service';
import { Observable, Subject } from 'rxjs';
import { first, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { AuthService } from 'app/shared/auth/auth.service';

@Component({
  selector: 'app-inference-settings-modal',
  templateUrl: './inference-settings-modal.component.html',
  styleUrls: ['./inference-settings-modal.component.scss']
})
export class InferenceSettingsModalComponent implements OnInit, OnDestroy {
  @ViewChild('promptEditorModal') private promptEditorTemplate: TemplateRef<any>;

  // --- COMPONENT STATE ---
  inferenceParams: InferenceParams;
  isDynamicBudget: boolean;
  modelConfig: ModelThinkingConfig;

  // --- OBSERVABLES ---
  models$: Observable<string[]>;
  templateNames$: Observable<string[]>;
  testGenPromptState$: Observable<PromptState>;
  isAdmin$: Observable<boolean>;

  // --- OTHER LOCAL PROPERTIES ---
  selectedTemplate: string;
  topN: number;
  testGenerationBatchSize: number;
  isCustomPromptForTestGen: boolean;
  promptToEdit: Prompt;

  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private sessioneService: StatoSessioneService,
    private promptService: PromptManagementService,
    private authService: AuthService
  ) {
    // Initialize observables in the constructor
    this.models$ = this.sessioneService.models$;
    this.templateNames$ = this.sessioneService.templates$.pipe(map(t => t.map(t => t.templateName)));
    this.testGenPromptState$ = this.promptService.getPromptState('testGeneration');
    this.isAdmin$ = this.authService.hasRole('Admin');
  }

  ngOnInit(): void {
    // 1. Sottoscrizione principale ai parametri.
    // Rimuoviamo l'impostazione di isDynamicBudget da qui.
    this.sessioneService.inferenceParams$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      this.inferenceParams = { ...params };
    });

    // 2. Sottoscrizione alla configurazione del modello.
    // Questa diventa la fonte di verità sia per modelConfig sia per isDynamicBudget.
    this.sessioneService.currentModelConfig$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(config => {
      this.modelConfig = config;

      // Impostiamo isDynamicBudget qui, dove abbiamo il contesto corretto.
      if (config.controlType === 'budget') {
        this.isDynamicBudget = this.inferenceParams.thinking_budget === -1;
      } else {
        this.isDynamicBudget = false; // Non è applicabile per 'level' o 'none'.
      }

      // La validazione si attiva solo quando necessario.
      this.validateBudgetAgainstNewConfig();
    });

    // --- Subscriptions for other settings (invariate) ---
    this.sessioneService.selectedTemplate$.pipe(takeUntil(this.destroy$)).subscribe(val => this.selectedTemplate = val);
    this.sessioneService.topN$.pipe(takeUntil(this.destroy$)).subscribe(val => this.topN = val);
    this.sessioneService.testGenerationBatchSize$.pipe(takeUntil(this.destroy$)).subscribe(val => this.testGenerationBatchSize = val);
    this.testGenPromptState$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      this.isCustomPromptForTestGen = state.isCustom;
    });
  }

  // --- Methods for Inference Parameters ---

  onDynamicBudgetToggle(): void {
    if (this.isDynamicBudget) {
      this.inferenceParams.thinking_budget = -1;
    } else {
      // Set to the minimum allowed value when turning off dynamic mode
      this.inferenceParams.thinking_budget = this.modelConfig.minBudget;
    }
    this.onParamsChange();
  }

  onModelChange(): void {
    const newConfig = this.sessioneService.getModelConfig(this.inferenceParams.model);

    // Pulisci i parametri del vecchio modello e imposta i default per il nuovo
    if (newConfig.controlType === 'level') {
      delete this.inferenceParams.thinking_budget;
      this.inferenceParams.thinking_level = 'high'; // Default raccomandato
      this.isDynamicBudget = false; // Non applicabile
    } else if (newConfig.controlType === 'budget') {
      delete this.inferenceParams.thinking_level;
      this.inferenceParams.thinking_budget = -1; // Default "Dynamic"
      this.isDynamicBudget = true;
    } else { // 'none'
      delete this.inferenceParams.thinking_budget;
      delete this.inferenceParams.thinking_level;
      this.isDynamicBudget = false;
    }

    this.onParamsChange(); // Invia lo stato aggiornato
  }

  // onParamsChange() rimane quasi uguale, ma lo chiameremo da onModelChange()
  onParamsChange(): void {
    this.sessioneService.updateInferenceParams(this.inferenceParams);
  }

  // NUOVO: Questo metodo viene chiamato quando cambiano le regole (cambio modello)
  private validateBudgetAgainstNewConfig(): void {
    // Do nothing if dynamic mode is on or if component isn't fully initialized yet
    if (this.isDynamicBudget || !this.inferenceParams) {
      return;
    }

    let currentBudget = this.inferenceParams.thinking_budget;
    let hasChanged = false;

    // Rule 1: Clamp to the new maximum if we are over
    if (currentBudget > this.modelConfig.maxBudget) {
      currentBudget = this.modelConfig.maxBudget;
      hasChanged = true;
    }

    // Rule 2: Clamp to the new minimum if we are under
    if (currentBudget < this.modelConfig.minBudget) {
      currentBudget = this.modelConfig.minBudget;
      hasChanged = true;
    }

    // If a change was made, update the local state and push it to the service
    if (hasChanged) {
      this.inferenceParams.thinking_budget = currentBudget;
      this.onParamsChange();
    }
  }

  // Questo metodo viene chiamato dall'input numerico (evento blur)
  validateBudgetInput(): void {
    if (this.isDynamicBudget) return;
    // Just reuse the new validation logic
    this.validateBudgetAgainstNewConfig();
  }

  // --- Methods for Other Settings (Unchanged) ---

  updateSelectedTemplate(): void {
    this.sessioneService.updateSelectedTemplate(this.selectedTemplate);
  }

  updateTopN(): void {
    if (this.topN < 1) this.topN = 1;
    this.sessioneService.updateTopN(this.topN);
  }

  updateBatchSize(): void {
    if (this.testGenerationBatchSize < 1) this.testGenerationBatchSize = 1;
    this.sessioneService.updateTestGenerationBatchSize(this.testGenerationBatchSize);
  }

  toggleCustomPrompt(): void {
    this.promptService.setCustomState('testGeneration', this.isCustomPromptForTestGen);
  }

  openPromptEditor(): void {
    this.testGenPromptState$.pipe(first()).subscribe(state => {
      const defaultPrompt = this.promptService.getDefaultPrompt('testGeneration');
      this.promptToEdit = {
        system: state.prompt.system || defaultPrompt.system,
        user: state.prompt.user || defaultPrompt.user
      };
    });
    this.modalService.open(this.promptEditorTemplate, { size: 'xl', scrollable: true })
      .result.then(
        (result) => { if (result === 'save') this.promptService.updateCustomPrompt('testGeneration', this.promptToEdit); },
        (reason) => { console.log('Prompt editor dismissed.'); }
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
